/**
 * Which of a question's options an agency's wording names. Exact first, then a declared alias
 * (`q.option_aliases`, for a legacy wording such as "SAG-AFTRA"), then the same letters and digits
 * ignoring case, accents, spacing and punctuation ("Non-Binary" names "Non-binary"). Read time only:
 * a stored value is never rewritten (Phases 1 to 5 transform no legacy value).
 */
export const YES_NO_OPTIONS = ['Yes', 'No'];
/** Letters and digits only, lowercased, accents folded: two wordings with one token name one option. */
export function choiceToken(s) {
    return s
        .normalize('NFKD')
        .replace(/\p{M}+/gu, '')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '');
}
/** The option `label` names among `options` (through `aliases` too), or null. */
export function matchChoice(options, label, aliases) {
    if (options.includes(label))
        return label;
    const alias = aliases && Object.hasOwn(aliases, label) ? aliases[label] : undefined;
    if (alias !== undefined && options.includes(alias))
        return alias;
    const token = choiceToken(label);
    if (!token)
        return null;
    return options.find((o) => choiceToken(o) === token) ?? null;
}
/**
 * Which of `options` a saved answer picks, for the chips that show it. A saved answer is an array of labels
 * or one string several labels were joined into ("Black; Irish"), and a label may differ from its option in
 * case, accents or punctuation ("Metis" for "Métis"): each part is matched like matchChoice. A part naming
 * no option is returned in `rest`, so the form can say it is saved but not on the list instead of showing
 * nothing. A comma splits a part only when every piece names an option, because an option may hold a comma.
 */
export function choiceSelection(options, value, aliases) {
    const parts = (Array.isArray(value) ? value : typeof value === 'string' ? value.split(/[;\n]+/) : [])
        .filter((p) => typeof p === 'string')
        .map((p) => p.trim())
        .filter(Boolean);
    const picked = [];
    const rest = [];
    const take = (o) => {
        if (!picked.includes(o))
            picked.push(o);
    };
    for (const part of parts) {
        const whole = matchChoice(options, part, aliases);
        if (whole !== null) {
            take(whole);
            continue;
        }
        const pieces = part.includes(',') ? part.split(',').map((s) => s.trim()).filter(Boolean) : [];
        const named = pieces.map((s) => matchChoice(options, s, aliases));
        if (pieces.length > 1 && named.every((m) => m !== null))
            named.forEach((m) => take(m));
        else
            rest.push(part);
    }
    return { picked, rest };
}
/** The labels among `labels` that name none of `options`. */
export const unmatchedChoices = (options, labels, aliases) => labels.filter((l) => matchChoice(options, l, aliases) === null);
/** What a question's answers are picked from: Yes and No, a list's options, a grid's rows. Empty for a
 *  free-text question (whatever suggestions it lists). */
export function questionChoices(q) {
    if (q.type === 'yes_no')
        return YES_NO_OPTIONS;
    if (q.type === 'select' || q.type === 'multi_select' || q.type === 'matrix')
        return q.options ?? [];
    return [];
}
/** A question each agency keeps to itself (owner agency or per_agency): its answers never leave the agency
 *  that holds them, so each agency may ask it with its own choices. */
export const agencyOwned = (q) => q.owner !== 'talent';
//# sourceMappingURL=choices.js.map